
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { EventBus } from '@/eventBus';
import { purchaseList } from '@/collections/purchase_list';
import {
  buildDialogGroups,
  buildInitialGroupsFromSelected,
  buildProductPlaceholderGroups,
  buildSubmitPayload,
  canSkipReplenishmentFetch,
  computeCanSubmit,
  computeSubmitLabel,
  hasFullSuggestionCoverage,
  hasPendingPlaceholders,
  hasUnresolvedVariantIds,
  mergeProductExpansion,
  mergeSuggestions,
  needsEnrichmentBeforeSubmit,
  resolveGroupsBeforeSubmit,
  resolvePendingSubmitPayload,
  startEnrichPhase,
  startSuggestionPhase,
  DialogGroup,
  AddToPurchaseListStrategy,
  AddToPurchaseListStrategyBuilder,
} from './AddToPurchaseListDialog';

export {
  buildDialogGroups,
  buildInitialGroupsFromSelected,
  buildProductPlaceholderGroups,
  buildSubmitPayload,
  canSkipReplenishmentFetch,
  computeCanSubmit,
  computeSubmitLabel,
  hasFullSuggestionCoverage,
  hasPendingPlaceholders,
  hasUnresolvedVariantIds,
  mergeProductExpansion,
  mergeSuggestions,
  needsEnrichmentBeforeSubmit,
  resolveGroupsBeforeSubmit,
  resolvePendingSubmitPayload,
  startEnrichPhase,
  startSuggestionPhase,
} from './AddToPurchaseListDialog';
export type { DialogGroup, DialogVariant, AddToPurchaseListStrategy, AddToPurchaseListStrategyBuilder } from './AddToPurchaseListDialog';

@Component({})
export default class AddToPurchaseListDialog extends Vue {
  @Prop({ default: false }) public value!: boolean;
  @Prop({ required: true }) public strategy!: AddToPurchaseListStrategyBuilder;
  @Prop({ default: () => [] }) public suppliers!: any[];
  @Prop({ default: 'Add to Purchase List' }) public title!: string;

  public open = false;
  public loading = false;             // Full-screen spinner — only used when we
                                       // genuinely have nothing to render yet
                                       // (i.e. the by_product flow before its
                                       // expansion fetch returns).
  public loadingSuggestions = false;   // Inline indicator inside the Suggested
                                       // column; renders alongside actual rows.
  public submitting = false;
  public errorMessage = '';
  public groups: DialogGroup[] = [];
  // Monotonic counter used to discard stale enrichment responses. Each refresh()
  // captures its own sequence; when enrich() resolves we only apply the merge if
  // the sequence is still current. Prevents the race where rapid close/reopen
  // lets an older enrich() overwrite the newer open's groups.
  private refreshSeq = 0;
  // Strategy instance captured at refresh() time so submit() can reuse its
  // enrich + mergeEnrichment functions for the lazy submit-time path
  // (when strategy.enrichAtSubmit is true). Rebuilding via this.strategy()
  // would discard user edits on the current groups.
  private activeStrategy: AddToPurchaseListStrategy | null = null;
  // AS-10567: the in-flight phase-1 fetch refresh() kicked off, parked so a
  // submit that fires before it lands can consume the SAME call instead of
  // issuing a second one. Null whenever nothing is in flight.
  private pendingEnrichRows: Promise<any[]> | null = null;

  public get totalCheckedWithQty(): number {
    let n = 0;
    for (const g of this.groups) {
      for (const v of g.variants) {
        const qty = v.quantity;
        if (v.checked && qty != null && Number.isFinite(qty) && qty > 0) { n++; }
      }
    }
    return n;
  }

  public get submitLabel(): string {
    return computeSubmitLabel(this.totalCheckedWithQty, this.groups);
  }

  public get canSubmit(): boolean {
    // Neither enrichment phase gates submission: phase 2 (suggestions) never
    // did, and since AS-10567 phase 1 doesn't either — a click on pending
    // placeholders hands the rest to submitInBackground() instead of
    // disabling the button or spinning it.
    return computeCanSubmit({
      loading: this.loading,
      submitting: this.submitting,
      errorMessage: this.errorMessage,
      totalCheckedWithQty: this.totalCheckedWithQty,
      groups: this.groups,
    });
  }

  @Watch('value', { immediate: true })
  public onValueChanged(v: boolean): void {
    this.open = v;
    if (v) { void this.refresh(); }
  }

  @Watch('open')
  public onOpenChanged(v: boolean): void {
    if (!v) { this.$emit('input', false); }
  }

  public async refresh(): Promise<void> {
    const thisSeq = ++this.refreshSeq;

    this.errorMessage = '';
    this.groups = [];
    this.loading = false;
    this.loadingSuggestions = false;
    this.pendingEnrichRows = null;

    const strategy: AddToPurchaseListStrategy = this.strategy();
    this.activeStrategy = strategy;
    this.groups = strategy.initialGroups;

    if (!strategy.enrich) { return; }
    // Lazy strategies defer the fetch to submit() — see the lazy-resolve
    // block below. refresh() must NOT run it at open time, or we'd lose
    // the fast-open win that motivated enrichAtSubmit.
    if (strategy.enrichAtSubmit) { return; }

    // Phase 1 — variant identity. The fetch is started here, not inside
    // startEnrichPhase, so submit() can hand the same promise to its
    // background path when the user clicks Add before it lands (AS-10567) —
    // one call, two consumers.
    const rows = strategy.enrich();
    this.pendingEnrichRows = rows;
    const enriched = await startEnrichPhase(
      strategy,
      rows,
      () => this.groups,
      (groups) => { this.groups = groups; },
      () => thisSeq === this.refreshSeq,
      (loading) => { this.loadingSuggestions = loading; },
      (message) => { this.errorMessage = message; },
    );
    // Only clear it if a newer refresh() hasn't already parked its own.
    if (thisSeq === this.refreshSeq) { this.pendingEnrichRows = null; }
    if (!enriched) { return; }

    // The user submitted (or cancelled) while phase 1 was in flight — the
    // dialog is gone, so don't spend the expensive forecast call on a
    // Suggested column nobody is looking at.
    if (!this.open) { this.loadingSuggestions = false; return; }

    // Phase 2 — reorder suggestions (AS-10567). Deliberately NOT awaited:
    // the user can already tick variants, type quantities and submit against
    // what phase 1 rendered. The Suggested column fills in behind them.
    const suggestionPhase = startSuggestionPhase(
      strategy,
      () => this.groups,
      (groups) => { this.groups = groups; },
      () => thisSeq === this.refreshSeq,
      (loading) => { this.loadingSuggestions = loading; },
    );
    // No phase 2 for this strategy — nothing more is coming, so stop the
    // inline spinner that phase 1 turned on.
    if (!suggestionPhase) { this.loadingSuggestions = false; }
  }

  public close(): void {
    this.open = false;
    this.$emit('input', false);
  }

  public async submit(): Promise<void> {
    if (!this.canSubmit) { return; }
    const strategy = this.activeStrategy;

    // AS-10567. Two things stop a click from adding anything right now:
    //   - by_product groups that are still placeholders (no variants yet), and
    //   - variants whose variant_id the report row never carried, which
    //     buildSubmitPayload drops — the click then looked like a dead button
    //     until the enrichment landed and swapped in rows that have real ids.
    // Both need a fetch, and the user explicitly does not want to wait for it:
    // close the dialog on the click and finish behind them. `pendingEnrichRows`
    // is reused when refresh() already has that fetch in flight, so the common
    // case costs no extra request.
    if (needsEnrichmentBeforeSubmit(this.groups)) {
      const rows = this.pendingEnrichRows
        ?? (strategy?.enrich ? strategy.enrich() : null);
      if (rows) {
        this.submitInBackground(rows, this.groups, strategy);
        this.close();
        return;
      }
    }

    // Everything the payload needs is already on screen — no round-trip, so
    // this path stays synchronous.
    this.submitting = true;
    try {
      const payload = buildSubmitPayload(this.groups, this.suppliers);
      if (payload.length === 0) {
        // Never fail silently: before AS-10567 this bare return was the
        // "nothing happens when I click Add" bug.
        EventBus.$emit('show-snackbar', {
          message: 'No variants found for the selected products',
          color: 'warning',
        });
        return;
      }
      const result = await purchaseList.addItems(payload);
      if (this.announceAddResult(result, payload.length) > 0) { this.close(); }
    } catch {
      EventBus.$emit('show-snackbar', { message: 'Failed to add items to purchase list', color: 'error' });
    } finally {
      this.submitting = false;
    }
  }

  // Finish a submit the user fired before phase 1 landed. Deliberately not
  // awaited by submit(): the dialog is already closing. `groups` is passed by
  // value because the dialog may be reopened against a different selection
  // before this resolves.
  private submitInBackground(
    rows: Promise<any[]>,
    groups: DialogGroup[],
    strategy: AddToPurchaseListStrategy | null,
  ): void {
    EventBus.$emit('show-snackbar', {
      message: 'Adding items to purchase list…',
      color: 'info',
    });
    void (async () => {
      try {
        const payload = await resolvePendingSubmitPayload(
          groups, rows, strategy?.mergeEnrichment, this.suppliers,
        );
        if (payload.length === 0) {
          EventBus.$emit('show-snackbar', {
            message: 'No variants found for the selected products',
            color: 'warning',
          });
          return;
        }
        const result = await purchaseList.addItems(payload);
        this.announceAddResult(result, payload.length);
      } catch {
        EventBus.$emit('show-snackbar', {
          message: 'Failed to add items to purchase list',
          color: 'error',
        });
      }
    })();
  }

  // Snackbar + `added` emit for a completed addItems call. Returns how many
  // items actually landed so the foreground path knows whether to close.
  private announceAddResult(result: any, total: number): number {
    const added = result?.added?.length ?? 0;
    const failed = result?.failedCount ?? 0;
    const viewAction = { label: 'View Purchase List', to: '/purchase-orders/purchase-list/review' };
    if (added === total) {
      EventBus.$emit('show-snackbar', {
        message: `Added ${added} item(s) to purchase list`,
        color: 'success',
        action: viewAction,
      });
    } else if (added > 0) {
      EventBus.$emit('show-snackbar', {
        message: `Added ${added} of ${total} item(s); ${failed} failed`,
        color: 'warning',
        action: viewAction,
      });
    } else {
      EventBus.$emit('show-snackbar', {
        message: 'Failed to add items to purchase list',
        color: 'error',
      });
    }
    if (added > 0) {
      this.$emit('added', { addedCount: added, totalCount: total, failedCount: failed });
    }
    return added;
  }

}
